import { type AlertEvent, AlertRulesResult, AlertsResult } from '@apm/contracts';
import { toQuery } from '../api';
import { useQuery } from '../hooks/useQuery';

/**
 * 알림 이력·규칙 조회 (10회차 결정 5, 19회차 규칙 목록).
 *
 * 경보 중인 것은 실시간으로 오지만 이력은 파일에 있다. 화면에 들어올 때 한 번 읽고,
 * 새 알림이 올 때마다(`version`) 다시 읽는다. 알림은 초당 수천 건이 아니라 하루에 몇 건이라
 * 그때마다 다시 읽어도 부담이 없다.
 */

/** `GET /api/alerts` 응답. 봉투는 계약에 있다 (17회차) */
export type { AlertRulesResult, AlertsResult };

export interface AlertsQuery {
  from?: number;
  to?: number;
  state?: string;
  severity?: string;
  ruleId?: string;
  service?: string;
  /**
   * `service`와 함께 쓴다. 참이면 서비스가 없는 공통 알림(DB 감시·폭주 억제·전체 규칙)도
   * 같은 응답에 온다. 행의 `service`가 비어 있는 것이 그것이다
   */
  includeGlobal?: boolean;
  limit?: number;
  enabled?: boolean;
  /** 이 값이 바뀌면 다시 읽는다 */
  version?: number;
}

export interface AlertsState {
  loading: boolean;
  rows: AlertEvent[];
  total: number;
  truncated: boolean;
  error?: string;
  reload: () => void;
}

export function useAlerts(query: AlertsQuery): AlertsState {
  const {
    from,
    to,
    state,
    severity,
    ruleId,
    service,
    includeGlobal,
    limit,
    version,
    enabled = true,
  } = query;
  const { loading, data, error, reload } = useQuery(
    enabled
      ? `/api/alerts${toQuery({
          from,
          to,
          state,
          severity,
          ruleId,
          service,
          includeGlobal: includeGlobal === true && service !== undefined ? '1' : undefined,
          limit,
        })}`
      : undefined,
    AlertsResult,
    version,
  );
  return {
    loading,
    rows: data?.rows ?? [],
    total: data?.total ?? 0,
    truncated: data?.truncated === true,
    error,
    reload,
  };
}

/**
 * 지금 물고 있는 규칙과 채널, 버린 규칙, 발송 카운터 (콘솔 검토 C-12).
 *
 * 서버는 16회차부터 이것을 다 주는데 콘솔이 부르지 않았다. 규칙 파일에 오타가 나서 감시가
 * 통째로 꺼져도 화면에는 티가 안 났다.
 */
export function useAlertRules(
  /** 이 값이 바뀌면 다시 읽는다. 관제 연결 상태 줄이 30초마다 올린다 (44회차) */
  version?: number,
  /** 거짓이면 부르지 않는다. 규칙은 관리자만 보는 자리다 (52회차) */
  enabled = true,
) {
  return useQuery(enabled ? '/api/alerts/rules' : undefined, AlertRulesResult, version);
}
